import { PaymentOptionPropertiesInterface } from '../../../services/exports/Interfaces';
import PaymentMethodBrandIcons, { getCardBrandIcon } from './PaymentMethodBrandIcons';
import classnames from 'classnames';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import { useMemo } from 'react';
import { PAYMENT_METHODS, PaymentMethod } from '../../../services/exports/Constants';
import { stripePaymentMethodTypeName } from '../../../services/helpers/stripePaymentMethodType';

interface Props extends Partial<PaymentOptionPropertiesInterface> {
  amount?: number;
  hideIcons?: boolean;
  stripe_payment_method_type?: null | string;
  classes?: {
    container?: string;
    icon?: string;
    text?: string;
  };
}

export default function PaymentDetails(props: Props) {
  const {
    payment_method_id,
    payment_method_brand,
    payment_method_details,
    stripe_payment_method_type,
    amount,
    hideIcons,
    classes,
  } = props;

  const { formatCurrency } = useAppMethods();
  const { findPaymentMethod } = usePaymentFlow();

  const hasBrandIcon = Boolean(getCardBrandIcon(payment_method_brand));
  // A past order can name a method the restaurant no longer offers, and `stripe_other` is never an
  // offered row, so the static list names what the company's options do not.
  const paymentMethod =
    findPaymentMethod(payment_method_id) ??
    PAYMENT_METHODS().find((row) => row.payment_method_id === payment_method_id);
  const paymentMethodName =
    (payment_method_id === PaymentMethod.StripeOther && stripePaymentMethodTypeName(stripe_payment_method_type)) ||
    paymentMethod?.display_name;
  const paymentMethodDetails = useMemo(() => {
    if (!payment_method_details) {
      return null;
    }

    return (
      {
        [PaymentMethod.Card]: `•••• ${payment_method_details}`,
        [PaymentMethod.ApplePay]: `•••• ${payment_method_details}`,
        [PaymentMethod.GooglePay]: `•••• ${payment_method_details}`,
        // A card paid through Link has its last 4; a plain Link payment only the customer's Link email.
        [PaymentMethod.Link]: payment_method_brand ? `•••• ${payment_method_details}` : payment_method_details,
        [PaymentMethod.Paypal]: payment_method_details,
      }[payment_method_id] ?? null
    );
  }, [payment_method_id, payment_method_brand, payment_method_details]);
  const hideName = payment_method_id === PaymentMethod.Paypal && Boolean(paymentMethodDetails);

  return (
    <div className={classnames('flex items-center text-sm min-w-0', classes?.container)}>
      {!hideIcons && (
        <PaymentMethodBrandIcons
          payment_method_id={payment_method_id}
          payment_method_brand={payment_method_brand}
          stripe_payment_method_type={stripe_payment_method_type}
          className={classes?.icon}
        />
      )}
      <p className={classnames('text-sm truncate', { 'ml-xs': !hideIcons }, classes?.text)}>
        {((!hasBrandIcon && Boolean(paymentMethodName)) || hideIcons) && !hideName && `${paymentMethodName} `}
        {Boolean(paymentMethodDetails) && ` ${paymentMethodDetails}`}
        {Boolean(amount) && ` - ${formatCurrency(amount)}`}
      </p>
    </div>
  );
}
